import ReactMarkdown from 'react-markdown'
import { Button, Orb, ReasoningBlock } from '@/components'
import { cn } from '@/lib/utils'
import { MESSAGE_STATUS } from '../../contexts/SSEEvents'
import type { MessageStatus } from '../api'

interface BotMessageAssistantProps {
  content: string
  /** The model's reasoning summary, when the turn ran with reasoning on. */
  reasoning?: string
  status?: MessageStatus
  streaming?: boolean
  error?: string
  onRetry?: () => void
  onCancel?: () => void
  hasActiveToolCall?: boolean
}

export default function BotMessageAssistant({
  content,
  reasoning,
  status,
  streaming,
  error,
  onRetry,
  onCancel,
  hasActiveToolCall = false,
}: BotMessageAssistantProps) {
  if (status === MESSAGE_STATUS.FAILED) {
    let errorMessage = 'An error occurred while processing your request.'
    if (content) {
      try {
        const parsed = JSON.parse(content)
        if (parsed && typeof parsed === 'object' && 'error' in parsed && parsed.error) {
          errorMessage = String(parsed.error)
        }
      } catch {
        if (content.length < 200) {
          errorMessage = content
        }
      }
    }
    if (error) {
      errorMessage = error
    }

    return (
      <div className="flex flex-col gap-2 pl-3 py-0.5 border-l-2 border-destructive/40">
        <div className="text-body text-foreground whitespace-pre-wrap break-words leading-relaxed">
          {errorMessage}
        </div>
        {(onRetry || onCancel) && (
          <div className="flex items-center gap-2">
            {onRetry && (
              <Button variant="outline" size="sm" className="text-label gap-1.5" onClick={onRetry}>
                Retry
              </Button>
            )}
            {onCancel && (
              <Button variant="ghost" size="sm" className="text-label" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
        )}
      </div>
    )
  }

  const isCanceled = status === MESSAGE_STATUS.CANCELED
  const isStreaming = Boolean(streaming)
  const hasReasoning = Boolean(reasoning?.trim())
  // The thinking orb fills the wait before the answer starts. Streaming reasoning
  // fills that same gap with something worth reading, so the orb would only be
  // noise beneath it.
  const isThinking =
    !content && !hasReasoning && !isCanceled
    && (isStreaming || status === MESSAGE_STATUS.IN_PROGRESS) && !hasActiveToolCall

  if (isThinking) {
    return (
      <div className="flex items-center gap-1.5 text-caption text-muted-foreground">
        <Orb state="thinking" size={20} aria-hidden />
        <span>Thinking</span>
      </div>
    )
  }

  // Reasoning with no answer yet is a real state, not an empty message: it is
  // what the whole thinking display exists to show.
  if (!content && !isCanceled && !hasReasoning) {
    return null
  }

  return (
    <div className="flex flex-col gap-1">
      {!isCanceled && (
        <ReasoningBlock
          reasoning={reasoning}
          streaming={isStreaming}
          hasContent={Boolean(content)}
        />
      )}
      <div
        className={cn(
          isCanceled ? 'line-through text-muted-foreground leading-relaxed' : 'leading-relaxed',
        )}
      >
        <span className={cn('text-body', !isCanceled && 'text-foreground')}>
          <span className="markdown-content break-words">
            <ReactMarkdown
              components={{
                p: ({ node: _node, ...props }) => (
                  <p className="my-3 first:mt-0 last:mb-0 whitespace-pre-wrap break-words" {...props} />
                ),
                ul: ({ node: _node, ...props }) => (
                  <ul className="my-3 list-disc pl-6 space-y-1" {...props} />
                ),
                ol: ({ node: _node, ...props }) => (
                  <ol className="my-3 list-decimal pl-6 space-y-1" {...props} />
                ),
                li: ({ node: _node, ...props }) => <li className="leading-relaxed" {...props} />,
                a: ({ node: _node, ...props }) => (
                  <a className="text-primary underline-offset-2 hover:underline" {...props} />
                ),
                code: ({ node: _node, ...props }) => {
                  const isInline = !props.className?.includes('language-')
                  return isInline ? (
                    <code
                      className="rounded-sm bg-muted px-1.5 py-0.5 text-mono-sm break-words"
                      {...props}
                    />
                  ) : (
                    <code className="block bg-muted rounded-md p-2 text-mono-sm overflow-x-auto whitespace-pre" {...props} />
                  )
                },
                pre: ({ node: _node, ...props }) => (
                  <pre className="my-3 bg-muted rounded-md overflow-x-auto" {...props} />
                ),
              }}
            >
              {content}
            </ReactMarkdown>
          </span>
        </span>
      </div>

      {isCanceled && (
        <span className="text-caption text-muted-foreground">(canceled)</span>
      )}
    </div>
  )
}
